import { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

// Action types match redux-oidc's shape so any stored state (localStorage) is compatible
const USER_FOUND = "redux-oidc/USER_FOUND";
const USER_EXPIRED = "redux-oidc/USER_EXPIRED";
const USER_SIGNED_OUT = "redux-oidc/USER_SIGNED_OUT";
const LOADING_USER = "redux-oidc/LOADING_USER";
const SILENT_RENEW_ERROR = "redux-oidc/SILENT_RENEW_ERROR";
const SESSION_TERMINATED = "redux-oidc/SESSION_TERMINATED";

// State shape identical to redux-oidc so all state.oidc.user reads continue unchanged
const initialState = {
  user: null,
  isLoadingUser: false,
};

export function oidcReducer(state = initialState, action) {
  switch (action.type) {
    case LOADING_USER:
      return { ...state, isLoadingUser: true };
    case USER_FOUND:
      return { ...state, user: action.payload, isLoadingUser: false };
    case USER_EXPIRED:
    case SESSION_TERMINATED:
    case USER_SIGNED_OUT:
      return { ...state, user: null, isLoadingUser: false };
    case SILENT_RENEW_ERROR:
      return { ...state, isLoadingUser: false };
    default:
      return state;
  }
}

// Loads the user from oidc-client-ts storage into Redux on app start
export async function loadUser(store, userManager) {
  store.dispatch({ type: LOADING_USER });
  try {
    const user = await userManager.getUser();
    if (user && !user.expired) {
      store.dispatch({ type: USER_FOUND, payload: user });
    } else {
      store.dispatch({ type: USER_EXPIRED });
    }
  } catch {
    store.dispatch({ type: USER_EXPIRED });
  }
}

// Subscribes to userManager events and keeps Redux state.oidc in sync
export function OidcProvider({ userManager, children }) {
  const dispatch = useDispatch();

  useEffect(() => {
    const onUserLoaded = (user) => dispatch({ type: USER_FOUND, payload: user });
    const onUserUnloaded = () => dispatch({ type: USER_SIGNED_OUT });
    const onSilentRenewError = () => dispatch({ type: SILENT_RENEW_ERROR });
    const onAccessTokenExpired = () => dispatch({ type: USER_EXPIRED });
    const onUserSignedOut = () => dispatch({ type: SESSION_TERMINATED });

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addSilentRenewError(onSilentRenewError);
    userManager.events.addAccessTokenExpired(onAccessTokenExpired);
    userManager.events.addUserSignedOut(onUserSignedOut);

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
      userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
      userManager.events.removeUserSignedOut(onUserSignedOut);
    };
  }, [userManager, dispatch]);

  return children;
}

OidcProvider.propTypes = {
  userManager: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};

// Drop-in replacement for redux-oidc's CallbackComponent
export function CallbackComponent({ userManager, successCallback, errorCallback, children }) {
  useEffect(() => {
    userManager
      .signinRedirectCallback()
      .then((user) => successCallback(user))
      .catch((err) => errorCallback && errorCallback(err));
  }, []);

  return children || null;
}

CallbackComponent.propTypes = {
  userManager: PropTypes.object.isRequired,
  successCallback: PropTypes.func.isRequired,
  errorCallback: PropTypes.func,
  children: PropTypes.node,
};
